import { Table } from '@tanstack/react-table';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/Domain/components/Shared/Button/Button';

type Props<TData> = {
  table: Table<TData>;
};

export const DataTablePagination = <TData,>({ table }: Props<TData>) => {
  const { t } = useTranslation('knowledge_base', { keyPrefix: 'articles.list.pagination' });

  const selectedRowsCount = table.getSelectedRowModel().rows.length;
  const totalRowsCount = table.getRowModel().rows.length;

  return (
    <div className="flex items-center justify-between px-2">
      <div className="flex-1 text-sm text-muted-foreground">
        {t('rows_of', { selectedRowsCount, totalRowsCount })}
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">
            {t('rows_per_page')} {table.getState().pagination.pageSize}
          </p>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          {t('page_of', {
            current: table.getPageCount() === 0 ? 0 : table.getState().pagination.pageIndex + 1,
            total: table.getPageCount(),
          })}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="white"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">{t('first_page')}</span>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="white"
            className="h-8 w-8 p-0"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">{t('previous_page')}</span>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="white"
            className="h-8 w-8 p-0"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">{t('next_page')}</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="white"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">{t('last_page')}</span>
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
